import { normalizePhone, type ProductDetail } from "@elektr/shared";
import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Textarea } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, ApiError, errorMessage, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

/** "So'rov qoldirish": mijoz ism, telefon va izoh qoldiradi; xodim keyin bog'lanadi. */
export function LeadDialog({
  product,
  open,
  onOpenChange,
}: {
  product: ProductDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("lead.title")}
      description={t("lead.description", { name: product.name })}
    >
      {/* forma faqat dialog ochilganda yaratiladi: boshlang'ich qiymatlar (ism) har safar yangidan olinadi */}
      <LeadForm product={product} onDone={() => onOpenChange(false)} />
    </Dialog>
  );
}

function LeadForm({ product, onDone }: { product: ProductDetail; onDone: () => void }) {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(user?.full_name ?? "");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: async () => {
      let normalized: string | undefined;
      if (phone.trim()) {
        const n = normalizePhone(phone);
        if (!n) throw new Error("phone");
        normalized = n;
      }
      return unwrap(
        await api.POST("/api/leads/", {
          body: {
            product: product.id,
            name,
            comment,
            ...(normalized ? { phone: normalized } : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      toast.success(t("lead.sent"));
      onDone();
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "phone") return setError(t("auth.phone_invalid"));
      if (e instanceof ApiError && e.status === 429) return setError(t("lead.too_many"));
      setError(errorMessage(e, t("common.error")));
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        send.mutate();
      }}
    >
      <Field label={t("lead.name")}>
        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label={t("lead.phone")} hint={t("lead.phone_hint", { phone: user?.phone ?? "" })}>
        {(id) => (
          <Input
            id={id}
            type="tel"
            inputMode="tel"
            placeholder="90 123 45 67"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        )}
      </Field>
      <Field label={t("lead.comment")}>
        {(id) => (
          <Textarea
            id={id}
            rows={3}
            maxLength={1000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        )}
      </Field>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={send.isPending}>
          {t("lead.send")}
        </Button>
      </div>
    </form>
  );
}
